/**
 * Source-pack files API endpoints — T20.
 * Maps PRD §9.4 to typed fetch calls via apiClient.
 */
import {
  type CreateSourcePackFileRequest,
  ListSourcePackFilesResponseSchema,
  SourcePackFileResponseSchema,
  type UpdateSourcePackFileRequest,
} from '@malaberg/shared';
import { apiClient } from '../client';

export interface ListSourcePackFilesQuery {
  limit?: number;
  offset?: number;
  /**
   * Narrow the list to files attachable to a specific product. The server
   * returns rows where scope = 'workspace' OR scope = `product:<productId>`.
   */
  productId?: string;
}

function toQueryString(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined);
  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}`;
}

export const sourcePackFilesApi = {
  list(query: ListSourcePackFilesQuery = {}) {
    return apiClient.request(
      'GET',
      `/api/source-pack-files${toQueryString(query as Record<string, string | number | undefined>)}`,
      {
        schema: ListSourcePackFilesResponseSchema,
      }
    );
  },

  get(id: string) {
    return apiClient.request('GET', `/api/source-pack-files/${id}`, {
      schema: SourcePackFileResponseSchema,
    });
  },

  getVersions(id: string) {
    return apiClient.request('GET', `/api/source-pack-files/${id}/versions`);
  },

  getVersion(id: string, version: number) {
    return apiClient.request('GET', `/api/source-pack-files/${id}/versions/${version}`, {
      schema: SourcePackFileResponseSchema,
    });
  },

  create(body: CreateSourcePackFileRequest) {
    return apiClient.request('POST', '/api/source-pack-files', {
      body: JSON.stringify(body),
    });
  },

  update(id: string, body: UpdateSourcePackFileRequest) {
    return apiClient.request('PUT', `/api/source-pack-files/${id}`, {
      body: JSON.stringify(body),
    });
  },

  revert(id: string, version: number) {
    return apiClient.request('POST', `/api/source-pack-files/${id}/revert/${version}`, {
      body: JSON.stringify({}),
    });
  },

  delete(id: string) {
    return apiClient.request('DELETE', `/api/source-pack-files/${id}`);
  },
};
